import { useState, useMemo } from "react";
import { Icon } from "semantic-ui-react";
import { makeStyles } from "@mui/styles";

import moment from "moment";
import { useIntl } from "react-intl";

import useLocationState from "./useLocationState";

import { getAddressDetail } from "react-lib/apps/MSG/StaffChatInterface";

type CardAddressInfoProps = {
  slot?: Record<string, any>;
  serviceName?: string;
  latlng?: string | null;
  onEvent?: (e: any) => any;
  // ข้อมูลนัด/สถานที่ (officialAccount, division_name, ...) — ส่งตรงจาก caller (MSG)
  // ถ้าไม่ส่งมา จะ fallback อ่านจาก shared location state (pattern ของ main app)
  locationState?: Record<string, any>;
};

const COLORS = {
  grey: "#3C3C3C",
};

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: "5px",
    padding: "12px 14px 10px",
    color: COLORS.grey,
    marginBottom: "12px",
    border: "1px solid rgba(217, 217, 217, 1)",
    lineHeight: 1.675,
  },
  address: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  title: {
    fontWeight: "bold",
    fontSize: "16px",
    marginBottom: "0.5rem",
  },
  locationButton: {
    width: "100%",
    padding: "7px 15px",
    border: "1px solid var(--primary-theme-color)",
    borderRadius: "12px",
    backgroundColor: "transparent",
    display: "flex",
    alignItems: "center",
    color: "var(--primary-theme-color)",
    fontSize: "14px",
    fontWeight: "bold",
    margin: "0.5rem 0",
  },
  bookingId: {
    color: "rgba(60, 60, 60, 1)",
    width: "fit-content",
    padding: "0.2rem 0.75rem",
    fontSize: "14px",
    margin: "0 -1rem",
    fontWeight: "normal",
    borderRadius: "6px 0 0 6px",
    backgroundColor: "rgba(255, 218, 230, 1)",
  },
  dateTimeRow: {
    display: "flex",
    gap: "24px",
    margin: "5px 0",
  },
  dateItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: COLORS.grey,
  },
  icon: {
    backgroundColor: "var(--oa-bg-color)",
    borderRadius: "500rem",
    minWidth: "22px",
    minHeight: "22px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    "& .icon": {
      color: "var(--primary-theme-color)",
      margin: 0,
      marginTop: "-8px",
    },
  },
}));

const IMAGES = {
  location: "/images/common/icon_location.png",
  provider: "/images/common/icon_provider.png",
};

const CardAddressInfo = (props: CardAddressInfoProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const sharedLocState = useLocationState<"AppointmentInfo">();
  // props ต้องมาก่อน shared state และต้องอ่านค่าใหม่ทุก render (เดิม memo [] → address ค้าง)
  const locState = props.locationState || sharedLocState;

  const [showAllDivision, setShowAllDivision] = useState(false);

  const addressDetail = useMemo(() => getAddressDetail(locState), [locState]);

  const showProvider = useMemo(() => {
    return !!locState.routes?.some((route: string) =>
      route.includes("/staff-schedule"),
    );
  }, [locState.routes]);

  const showAddressIcon = useMemo(() => {
    return showProvider;
  }, [showProvider]);

  const formattedDate = useMemo(() => {
    if (props.slot?.estimated_at_iso) {
      const momentDate = moment(props.slot.estimated_at_iso);
      const year = momentDate.year();
      const dayMonth = momentDate.format("DD MMMM");

      return `${dayMonth} ${year + 543}`;
    }

    return "-";
  }, [props.slot]);

  const handleOpenMap = (lat: number, lng: number) => {
    props.onEvent?.({
      message: "HandleOpenMap",
      params: { lat, lng },
    });
  };

  return (
    <div className={classes.card} style={{ marginTop: "1rem" }}>
      {props.serviceName ? (
        <div className={classes.title} style={{ color: "rgba(243, 130, 160, 1)" }}>
          {props.serviceName}
        </div>
      ) : (
        props.slot?.appointment_id && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <div className={classes.title} style={{ fontSize: "15px", margin: 0 }}>
              {intl.formatMessage({ id: "รายละเอียดนัดหมาย" })}
            </div>
            <div className={classes.bookingId}>BookingID : {props.slot?.appointment_id}</div>
          </div>
        )
      )}

      <div>
        <div style={props.serviceName ? {} : { fontWeight: "bold" }}>
          {intl.formatMessage({ id: "วันเวลาที่นัดหมาย" })}
        </div>
        <div className={classes.dateTimeRow}>
          <div className={classes.dateItem}>
            <div className={classes.icon}>
              <Icon name="calendar alternate outline" />
            </div>
            {formattedDate}
          </div>
          <div className={classes.dateItem}>
            <div className={classes.icon}>
              <Icon name="clock" />
            </div>
            {props.slot?.estimated_at_iso
              ? moment(props.slot.estimated_at_iso).format("HH:mm")
              : "-"}
          </div>
        </div>
      </div>
      <>
        {!props.serviceName && (
          <>
            <div style={{ fontWeight: "bold" }}>
              {intl.formatMessage({ id: "สถานที่รับบริการ" })}
            </div>
            <div>{addressDetail.name}</div>
          </>
        )}
      </>

      {props.latlng && (
        <button
          className={classes.locationButton}
          onClick={() => handleOpenMap(addressDetail.latitude, addressDetail.longitude)}
        >
          <img
            src={IMAGES.location}
            style={{ width: "21px", marginRight: "0.75rem" }}
            alt="location.icon"
          />
          <span>{intl.formatMessage({ id: "ดูข้อมูล Maps Location" })}</span>
        </button>
      )}
    </div>
  );
};

export default CardAddressInfo;
